import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, mergeProps as _mergeProps, withCtx as _withCtx, createSlots as _createSlots } from "vue"

import { is, QInput, ValidationRule } from "quasar"
import SfxBtnUndo from "src/components/button/SfxBtnUndo.vue"
import { getPrecision, isEmptyOrNull } from "src/util/functions/misc"
import { computed, ref, useAttrs } from "vue"
import { useI18n } from "vue-i18n"

//region Props and Emits

interface Props {
  // If input is required, and no input is provided, it will display an error.
  required?: boolean
  disable?: boolean
  rules?: ValidationRule[]
  originalValue?: string | number | null | undefined
  isOriginalValue?: boolean
}


export default /*#__PURE__*/_defineComponent({
  __name: 'SfxInput',
  props: /*#__PURE__*/_mergeModels({
    required: { type: Boolean, default: false },
    disable: { type: Boolean, default: false },
    rules: { default: () => [] },
    originalValue: { default: undefined },
    isOriginalValue: { type: Boolean, default: false }
  }, {
    "modelValue": {},
    "modelModifiers": {},
  }),
  emits: ["update:modelValue"],
  setup(__props: any, { expose: __expose }) {

const props = __props

const modelValue = _useModel<string | number | null | undefined>(__props, "modelValue")

const attrs = useAttrs()

__expose({ validate })

//endregion

//region Variables

const { t } = useI18n()
const inputRef = ref<QInput>()

const isEqualToOriginal = computed(() => {
  if (props.isOriginalValue !== undefined) {
    return props.isOriginalValue
  }
  if (props.originalValue === undefined) {
    return true
  }

  return is.deepEqual(props.originalValue, modelValue.value)
})

const edited = computed(() => props.originalValue && !props.disable && !isEqualToOriginal.value)

const classes = computed(() => ({
  "sfx-input": true,
  "q-field__required": props.required,
  "q-field__edited": !props.disable && !isEqualToOriginal.value,
}))

const rules = computed<ValidationRule[]>(() => {
  return [
    ...props.rules,
    ...numberRules.value,
    ...charRules.value,
    props.required ? (val: unknown) => Boolean(val) || t("sfxInput.field_empty") : undefined,
  ]
})

//endregion

//region Functions

function validate() {
  return inputRef.value?.validate()
}

const numberRules = computed<ValidationRule[]>(() => {
  const rules: ValidationRule[] = []

  const type = attrs["attr-type"]

  if (type && type === "numeric") {
    if (precisionRules.value) rules.push(precisionRules.value)

    if (minMaxRules.value) rules.push(minMaxRules.value)
  }

  return rules
})

const minMaxRules = computed<ValidationRule | undefined>(() => {
  const max = attrs["maxVal"] as string
  const min = attrs["minVal"] as string

  if (max != null && min != null) {
    return (val: string) =>
      isEmptyOrNull(val) ||
      (val >= min && val <= max) ||
      t("sfxInput.error_number_between", { min, max })
  } else if (max != null) {
    return (val: string) =>
      isEmptyOrNull(val) || val <= max || t("sfxInput.error_number_max", { max })
  } else if (min != null) {
    return (val: string) =>
      isEmptyOrNull(val) || val >= min || t("sfxInput.error_number_min", { min })
  }

  return undefined
})

const precisionRules = computed<ValidationRule | undefined>(() => {
  const decimals = attrs["decimals"] as string

  if (decimals != null) {
    return (val: string) =>
      isEmptyOrNull(val) ||
      getPrecision(val)?.toString() ||
      0 <= Number(decimals) ||
      (Number(decimals) === 0
        ? t("sfxInput.error_number_whole")
        : t("sfxInput.error_number_precision", { decimals }))
  }

  return undefined
})

const charRules = computed(() => {
  const rules: ValidationRule[] = []
  const type = attrs["attr-type"]

  if (type && type === "char") {
    const allowBlank = attrs["allow_blank"]

    if (!allowBlank) {
      rules.push(
        (val: string) => isEmptyOrNull(val) || val.trim() !== "" || t("sfxInput.error_char_blanks")
      )
    }
  }

  return rules
})

//endregion

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(QInput), _mergeProps({
    ref_key: "inputRef",
    ref: inputRef,
    outlined: "",
    color: "secondary",
    rules: rules.value,
    "no-error-icon": "",
    disable: props.disable
  }, _ctx.$attrs, {
    class: classes.value,
    modelValue: modelValue.value,
    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((modelValue).value = $event))
  }), _createSlots({ _: 2 }, [
    (_ctx.$slots.default)
      ? {
          name: "default",
          fn: _withCtx(() => [
            _renderSlot(_ctx.$slots, "default")
          ]),
          key: "0"
        }
      : undefined,
    (_ctx.$slots.prepend)
      ? {
          name: "prepend",
          fn: _withCtx(() => [
            _renderSlot(_ctx.$slots, "prepend")
          ]),
          key: "1"
        }
      : undefined,
    (_ctx.$slots.append)
      ? {
          name: "append",
          fn: _withCtx(() => [
            _renderSlot(_ctx.$slots, "append")
          ]),
          key: "2"
        }
      : undefined,
    (_ctx.$slots.before)
      ? {
          name: "before",
          fn: _withCtx(() => [
            _renderSlot(_ctx.$slots, "before")
          ]),
          key: "3"
        }
      : undefined,
    (_ctx.$slots.label)
      ? {
          name: "label",
          fn: _withCtx(() => [
            _renderSlot(_ctx.$slots, "label")
          ]),
          key: "4"
        }
      : undefined,
    (_ctx.$slots.error)
      ? {
          name: "error",
          fn: _withCtx(() => [
            _renderSlot(_ctx.$slots, "error")
          ]),
          key: "5"
        }
      : undefined,
    (_ctx.$slots.hint)
      ? {
          name: "hint",
          fn: _withCtx(() => [
            _renderSlot(_ctx.$slots, "hint")
          ]),
          key: "6"
        }
      : undefined,
    (_ctx.$slots.counter)
      ? {
          name: "counter",
          fn: _withCtx(() => [
            _renderSlot(_ctx.$slots, "counter")
          ]),
          key: "7"
        }
      : undefined,
    (_ctx.$slots.loading)
      ? {
          name: "loading",
          fn: _withCtx(() => [
            _renderSlot(_ctx.$slots, "loading")
          ]),
          key: "8"
        }
      : undefined,
    (_ctx.$slots.after || _ctx.originalValue)
      ? {
          name: "after",
          fn: _withCtx(() => [
            (edited.value)
              ? (_openBlock(), _createBlock(SfxBtnUndo, {
                  key: 0,
                  "original-value": _ctx.originalValue,
                  modelValue: modelValue.value,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((modelValue).value = $event))
                }, null, 8, ["original-value", "modelValue"]))
              : _renderSlot(_ctx.$slots, "after", { key: 1 })
          ]),
          key: "9"
        }
      : undefined
  ]), 1040, ["rules", "disable", "class", "modelValue"]))
}
}

})